<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from "vue";
import Table from "../../../base-components/Table";
import Preview from "../../../base-components/Preview";
import Lucide from "../../../base-components/Lucide/Lucide.vue";
import { usePedidosStore } from "../../../services/pedidos";
import { userUserStore } from "../../../stores/user";
import { getFirstDayOfMonth } from "../../../utils/helper";
import { getCurrentDate } from "../../../utils/fecha";
import { storeToRefs } from "pinia";
import ExcelJS from "exceljs";
import {
  collection,
  getDocs,
  getFirestore,
  query,
  Timestamp,
  where
} from "firebase/firestore";
import ReportBarChart1 from "../../../components/ReportBarChart1/ReportBarChart1.vue";

const pedidosStore = usePedidosStore();
const { filtros } = storeToRefs(pedidosStore);
const userStore = userUserStore();

const db = getFirestore();

const cargando = ref(false);

watch(
  () => [filtros.value.fechaInicio, filtros.value.fechaFin],
  async ([inicio, fin]) => {
    if (!inicio || !fin) return;

    cargando.value = true;
    await cargarRubros();
    cargando.value = false;
  }
);
const estadisticasCategorias = ref<any[]>([]);
const parseFecha = (fecha: string) => {
  const [dia, mes, anio] = fecha.split("/");
  return new Date(Number(anio), Number(mes) - 1, Number(dia));
};
const cargarRubros = async () => {
  const inicio = parseFecha(filtros.value.fechaInicio);
  const fin = parseFecha(filtros.value.fechaFin);

  fin.setHours(23, 59, 59, 999);

  const q = query(
    collection(db, `empresas/${userStore.empresa?.id}/estadisticas-categorias`),
    where("fecha", ">=", Timestamp.fromDate(inicio)),
    where("fecha", "<=", Timestamp.fromDate(fin))
  );

  const snap = await getDocs(q);

  const resultados: any[] = [];

  snap.forEach(doc => {
    resultados.push(doc.data());
  });

  estadisticasCategorias.value = resultados;
};

const categoriasAgrupadas = computed(() => {
  const mapa: Record<
    string,
    {
      rubro: string;
      cantidad: number;
      monto: number;
      descuento: number;
    }
  > = {};

  for (const item of estadisticasCategorias.value) {
    const key = item.rubro;

    if (!mapa[key]) {
      mapa[key] = {
        rubro: key,
        cantidad: 0,
        monto: 0,
        descuento: 0
      };
    }

    mapa[key].cantidad += item.cantidad;
    mapa[key].monto += item.monto;
    mapa[key].descuento += item.descuento || 0;
  }

  return Object.values(mapa).sort((a, b) => b.monto - a.monto);
});

const chartData = computed(() => {
  const result: Record<string, number> = {};

  for (const doc of estadisticasCategorias.value) {
    const key = doc.rubro || "Sin categoría";

    if (!result[key]) {
      result[key] = 0;
    }

    if (indexGrafica.value === 0) {
      result[key] += Number(doc.cantidad) || 0;
    } else if (indexGrafica.value === 1) {
      result[key] += Number(doc.monto) || 0;
    } else if (indexGrafica.value === 2) {
      result[key] += Number(doc.descuento) || 0;
    }
  }

  return Object.fromEntries(
    Object.entries(result)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
  );
});
onBeforeMount(async () => {
  filtros.value.fechaInicio = getFirstDayOfMonth();
  filtros.value.fechaFin = getCurrentDate();
  cargando.value = true;
  await cargarRubros();
  cargando.value = false;
});

const handleExport = async () => {
  const workbook = new ExcelJS.Workbook();

  const worksheet = workbook.addWorksheet("Hoja 1");
  const encabezados = [
    "Categoría",
    "Cantidad de ventas",
    "Monto ventas (incluye descuentos)",
    "Descuentos"
  ];
  worksheet.addRow(encabezados);

  for (const estadistica of categoriasAgrupadas.value) {
    const datos = [
      estadistica.rubro,
      estadistica.cantidad,
      estadistica.monto.toFixed(2),
      estadistica.descuento.toFixed(2)
    ];
    worksheet.addRow(datos);
  }

  workbook.xlsx.writeBuffer().then(buffer => {
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Estadísticas categorías - ${filtros.value.fechaInicio}- ${filtros.value.fechaFin}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  });
};

const indexGrafica = ref(0);
</script>

<template>
  <Preview class="mt-4 box">
    <Preview.Panel>
      <div class="px-5 min-w-0 my-6">
        <h2 class="my-3 text-base ml-1">Reporte por categorías</h2>
        <div
          class="overflow-y-visible min-w-0 -mx-5 px-5 grid grid-cols-3 gap-6"
        >
          <div class="col-span-2 max-h-[300px] overflow-y-auto">
            <Table bordered hover striped class="min-w-[700px]">
              <Table.Thead class="bg-[#E09C2D] text-white">
                <Table.Tr>
                  <Table.Th class="whitespace-nowrap"> Categoría</Table.Th>
                  <Table.Th class="whitespace-nowrap">
                    Cantidad de ventas</Table.Th
                  >
                  <Table.Th class="whitespace-nowrap">
                    Monto ventas (incluye descuentos)</Table.Th
                  >
                  <Table.Th class="whitespace-nowrap"> Descuentos </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <template
                  v-for="estadistica in categoriasAgrupadas"
                  :key="estadistica.rubro"
                >
                  <Table.Tr
                    class="[&:hover_td]:bg-[#E09C2D33]/5 [&:nth-of-type(odd)_td]:bg-[#E09C2D33]/20"
                  >
                    <Table.Td> {{ estadistica.rubro }} </Table.Td>
                    <Table.Td>{{ estadistica.cantidad }} </Table.Td>
                    <Table.Td>{{ estadistica.monto.toFixed(2) }}</Table.Td>
                    <Table.Td>{{ estadistica.descuento.toFixed(2) }}</Table.Td>
                  </Table.Tr>
                </template>
                <Table.Tr
                  class="[&:hover_td]:bg-[#E09C2D33]/5 [&:nth-of-type(odd)_td]:bg-[#E09C2D33]/20"
                  v-if="estadisticasCategorias.length === 0"
                >
                  <Table.Td
                    colspan="30"
                    class="text-center py-8 text-slate-500"
                  >
                    No hay estadísticas para mostrar.
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </div>

          <div
            class="col-span-1 flex-1 bg-white/25 py-8 px-6 border rounded-xl border-[#810C5133]/20 shadow-xl justify-center overflow-x-auto"
          >
            <div
              class="border border-[#810C5133]/20 shadow-xl p-1 mb-8 rounded-xl w-fit mx-auto border-slate-300 dark:border-darkmode-300 bg-[#FFFFFF40]/25 flex p-3"
            >
              <button
                class="w-full p-3 rounded-xl text-black font-normal"
                :class="{ 'bg-[#E09C2D33]/20': indexGrafica === 0 }"
                @click="indexGrafica = 0"
              >
                Cantidad
              </button>

              <button
                class="w-full p-3 rounded-xl text-black font-normal"
                :class="{ 'bg-[#E09C2D33]/20': indexGrafica === 1 }"
                @click="indexGrafica = 1"
              >
                Monto
              </button>

              <button
                class="w-full p-3 rounded-xl text-black font-normal"
                :class="{ 'bg-[#E09C2D33]/20': indexGrafica === 2 }"
                @click="indexGrafica = 2"
              >
                Descuentos
              </button>
            </div>

            <div class="w-full max-w-[500px] mx-auto">
              <ReportBarChart1 :dataMap="chartData" :height="300" />
            </div>
          </div>
        </div>
        <div
          @click="handleExport"
          class="flex relative group mt-6 cursor-pointer ml-1"
        >
          <Lucide icon="Download" class="w-4 h-4 mr-2" />
          <p>Exportar datos a Excel</p>
        </div>
      </div>
    </Preview.Panel>
  </Preview>
</template>
